<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-container>
    <h3 class="mt-4">Additional Scripts</h3>
    <v-row class="mt-2">
      <v-col cols="6">
        <v-select
          label="Script to run"
          v-model="selectedScript"
          :items="scripts"
          item-text="name"
          item-value="value"
          clearable
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
            </v-list-item-content>
            <v-chip
              x-small
              :color="item.readOnly ? 'success' : 'warning'"
              dark
            >{{ item.readOnly ? 'Read-only' : 'Writes data' }}</v-chip>
          </template>
        </v-select>
      </v-col>
      <v-col cols="3" class="pt-6">
        <v-chip
          v-if="selectedScriptInfo"
          small
          :color="effectiveIsReadOnly ? 'success' : 'warning'"
          dark
        >{{ effectiveIsReadOnly ? 'Read-only — no data will change' : 'Writes data — modifies records' }}</v-chip>
      </v-col>
      <v-col cols="3" class="pt-6">
        <em>County (from URL): {{ countyName || 'unknown (local/dev host)' }}</em>
      </v-col>
    </v-row>

    <!-- Toggle shown only for scripts that support a matching write-mode fix
         (currently check-school-district-mismatch -> fix-school-district-mismatch) -->
    <v-row v-if="showFixModeToggle" class="mt-0">
      <v-col cols="6">
        <v-checkbox
          v-model="fixMode"
          label="Also fix (write mode)"
          dense
          hide-details
        ></v-checkbox>
        <em>Unchecked: read-only, just reports mismatches. Checked: relabels the stale
          schoolDistrict on payment/adjustment records and shifts the $ amount in
          taxRollSummary from the old district to the new one.</em>
      </v-col>
    </v-row>

    <!-- Filters shown only for scripts that support a date-range filter -->
    <v-row v-if="showDateFilters" class="mt-0">
      <v-col cols="3">
        <v-menu
          v-model="fromDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="fromDate"
              label="From Date (optional)"
              readonly
              clearable
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="fromDate" @input="fromDateMenu = false"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="3">
        <v-menu
          v-model="toDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="toDate"
              label="To Date (optional)"
              readonly
              clearable
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="toDate" @input="toDateMenu = false"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="6" class="pt-5">
        <em>Leave both blank to run against every matching record in this county.</em>
      </v-col>
    </v-row>

    <!-- Filter shown only for scripts that support scanning tax years in chunks
         (currently check-school-district-mismatch) -->
    <v-row v-if="showYearRangeFilter" class="mt-0">
      <v-col cols="3">
        <v-select
          label="Tax Year Range"
          v-model="selectedYearRangeIndex"
          :items="yearRangeOptions"
          item-text="label"
          item-value="index"
        ></v-select>
      </v-col>
      <v-col cols="6" class="pt-5">
        <em>Runs 5 tax years at a time to keep each pass fast. Pick "All years" to scan everything in one run.</em>
      </v-col>
    </v-row>

    <v-row class="mt-0">
      <v-col cols="3">
        <v-btn
          color="primary"
          :disabled="!selectedScript || isRunning"
          :loading="isRunning"
          @click="runScript"
        >Run Script</v-btn>
      </v-col>
    </v-row>

    <v-row v-if="scriptError">
      <v-col cols="12">
        <v-alert type="error" outlined dense>
          {{ scriptError }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- Fixed vs unfixed status table (currently only check-school-district-mismatch
         returns rows). Reflects every parcel ever flagged, not just this run. -->
    <v-row v-if="resultRows.length" class="mt-4">
      <v-col cols="12">
        <h4>Results — {{ selectedScriptLabel }}</h4>
        <v-data-table
          :headers="activeResultHeaders"
          :items="resultRows"
          item-key="_rowKey"
          dense
          disable-pagination
          hide-default-footer
        >
          <template #item.staleDistricts="{ item }">
            {{ (item.staleDistricts || []).join(', ') }}
          </template>
          <template #item.currentSchoolDistrict="{ item }">
            <v-chip
              v-if="!item.currentSchoolDistrict"
              small
              color="warning"
              title="Parcel has no schoolDistrict set. Not auto-fixable -- enter the parcel's district manually first."
            >No parcel district — not auto-fixable</v-chip>
            <span v-else>{{ item.currentSchoolDistrict }}</span>
          </template>
          <template #item.status="{ item }">
            <v-chip :color="item.status === 'fixed' ? 'success' : 'error'" small dark>
              {{ item.status }}
            </v-chip>
          </template>
          <template #item.firstFlaggedAt="{ item }">
            {{ formatDate(item.firstFlaggedAt) }}
          </template>
          <template #item.fixedAt="{ item }">
            {{ formatDate(item.fixedAt) }}
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Run history: one row per county this script has been run against.
         Refetched after every run, so a new county adds a row automatically. -->
    <v-row v-if="showRunLog" class="mt-4">
      <v-col cols="12">
        <h4>Run History — {{ selectedScriptLabel }}</h4>
        <v-data-table
          :headers="runLogHeaders"
          :items="runLog"
          :loading="isLoadingLog"
          item-key="_rowKey"
          dense
          disable-pagination
          hide-default-footer
        >
          <template #item.ranAt="{ item }">
            {{ formatDate(item.ranAt) }}
          </template>
          <template #item.status="{ item }">
            <v-chip :color="item.status === 'success' ? 'success' : 'error'" small dark>
              {{ item.status }}
            </v-chip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

// Scripts that accept an optional payDate range filter (fromDate/toDate on
// truncateInputType). Every other script in `scripts` below ignores the
// filter fields, same as the backend dispatcher does.
const SCRIPTS_WITH_DATE_FILTER = ['reset-is-apportioned-flag'];

// Scripts that accept an optional taxYear-range filter (fromYear/toYear on
// truncateInputType) so a large county can be scanned a few years at a time
// instead of one all-years pass.
const SCRIPTS_WITH_YEAR_RANGE_FILTER = ['check-school-district-mismatch'];

// Scripts that record a per-county completion entry (see additional-script.
// util.js: logScriptRun / getScriptRunLog) and so have a run history to show.
const SCRIPTS_WITH_RUN_LOG = ['reset-is-apportioned-flag', 'update-additional-data-for-ptw', 'overpayment-paymentmethod-backfill'];

// Scripts whose `rows` aren't the school-district-mismatch row shape - each
// gets its own result table headers.
const SCRIPTS_WITH_CUSTOM_ROWS = ['overpayment-paymentmethod-backfill'];

// Scripts that have a write-mode counterpart toggled by the "Also fix" checkbox
// instead of a separate dropdown entry. Maps the read-only script's value to the
// collectionName the backend dispatcher expects when the checkbox is checked.
const SCRIPT_FIX_MODE_MAP = {
  'check-school-district-mismatch': 'fix-school-district-mismatch',
};

const CURRENT_YEAR = new Date().getFullYear();
const OLDEST_TAX_YEAR = 1990;

// 5-year buckets, most recent first, plus an "All years" option with no filter.
const buildYearRangeOptions = () => {
  const options = [{
    index: 0, label: 'All years', fromYear: null, toYear: null,
  }];
  let index = 1;
  for (let end = CURRENT_YEAR + 1; end > OLDEST_TAX_YEAR; end -= 5) {
    const start = end - 4;
    options.push({
      index, label: `${start}–${end}`, fromYear: start, toYear: end,
    });
    index += 1;
  }
  return options;
};

// Same pattern used in util/clients/client-config.js to resolve county-scoped
// backend URLs: prod hosts are https://<county>.local.tmconsulting.dev.
const PROD_COUNTY_URL_RE = /(?:http[s]?):\/\/([a-zA-Z0-9-]*)\.local\.tmconsulting\.dev/;

export default {
  name: 'AdditionalScripts',
  data() {
    return {
      selectedScript: null,
      fromDate: null,
      toDate: null,
      fromDateMenu: false,
      toDateMenu: false,
      yearRangeOptions: buildYearRangeOptions(),
      selectedYearRangeIndex: 0,
      fixMode: false,
      isRunning: false,
      isLoadingLog: false,
      scriptError: '',
      resultRows: [],
      mismatchStatusHeaders: [
        { text: 'Tax Year', value: 'taxYear' },
        { text: 'Tax ID', value: 'taxId' },
        { text: 'Base ID', value: 'baseId' },
        { text: 'Current District', value: 'currentSchoolDistrict' },
        { text: 'Stale District(s)', value: 'staleDistricts' },
        { text: 'Mismatched Payments', value: 'paymentMismatchCount' },
        { text: 'Mismatched Adjustments', value: 'adjustmentMismatchCount' },
        { text: 'Status', value: 'status' },
        { text: 'First Flagged', value: 'firstFlaggedAt' },
        { text: 'Fixed At', value: 'fixedAt' },
      ],
      overpaymentBackfillHeaders: [
        { text: 'Tax Year', value: 'taxYear' },
        { text: 'Tax ID', value: 'taxId' },
        { text: 'Parcel ID', value: 'propertyOrParcelId' },
        { text: 'Base ID', value: 'baseId' },
        { text: 'Payment Method', value: 'paymentMethod' },
      ],
      runLog: [],
      runLogHeaders: [
        { text: 'County', value: 'county' },
        { text: 'Ran At', value: 'ranAt' },
        { text: 'From Date', value: 'fromDate' },
        { text: 'To Date', value: 'toDate' },
        { text: 'Flagged', value: 'flaggedCount' },
        { text: 'Cleared', value: 'clearedCount' },
        { text: 'Status', value: 'status' },
        { text: 'Error', value: 'error' },
      ],
      // readOnly: true means the script only reads data (safe to run any time, no
      // confirmation needed). readOnly: false/omitted means it writes to the
      // database and the user is asked to confirm before running it.
      scripts: [
        {
          name: 'Reset isApportioned Flag (Lock bug fix)', value: 'reset-is-apportioned-flag', readOnly: false,
        },
        {
          name: 'Recover Deleted Fee', value: 'recover-deleted-fee', readOnly: false,
        },
        {
          name: 'Fix Owner Name In Care', value: 'fix-owner-name', readOnly: false,
        },
        {
          name: 'Settle Negative Fees', value: 'settle-all-negative-fee', readOnly: false,
        },
        {
          name: 'Update taxinquiry in PTW', value: 'update-taxinquiry-in-ptw', readOnly: false,
        },
        {
          name: 'Check School District Mismatch (payments/adjustments vs parcel)', value: 'check-school-district-mismatch', readOnly: true,
        },
        {
          name: 'Update additional data for PTW', value: 'update-additional-data-for-ptw',
        },
        {
          name: 'Backfill Overpayment Payment Method (TKT-000603)', value: 'overpayment-paymentmethod-backfill', readOnly: false,
        },
      ],
    };
  },
  computed: {
    activeResultHeaders() {
      return SCRIPTS_WITH_CUSTOM_ROWS.includes(this.selectedScript)
        ? this.overpaymentBackfillHeaders : this.mismatchStatusHeaders;
    },
    showDateFilters() {
      return SCRIPTS_WITH_DATE_FILTER.includes(this.selectedScript);
    },
    showYearRangeFilter() {
      return SCRIPTS_WITH_YEAR_RANGE_FILTER.includes(this.selectedScript);
    },
    selectedYearRange() {
      return this.yearRangeOptions.find((r) => r.index === this.selectedYearRangeIndex) || null;
    },
    showRunLog() {
      return SCRIPTS_WITH_RUN_LOG.includes(this.selectedScript);
    },
    selectedScriptLabel() {
      const found = this.scripts.find((s) => s.value === this.selectedScript);
      return found ? found.name : this.selectedScript;
    },
    selectedScriptInfo() {
      return this.scripts.find((s) => s.value === this.selectedScript) || null;
    },
    showFixModeToggle() {
      return Object.prototype.hasOwnProperty.call(SCRIPT_FIX_MODE_MAP, this.selectedScript);
    },
    effectiveIsReadOnly() {
      if (this.showFixModeToggle) {
        return !this.fixMode;
      }
      return !!(this.selectedScriptInfo && this.selectedScriptInfo.readOnly);
    },
    countyName() {
      const matches = window.location.href.match(PROD_COUNTY_URL_RE);
      return matches ? matches[1] : null;
    },
  },
  watch: {
    selectedScript(newVal) {
      this.scriptError = '';
      this.resultRows = [];
      this.fixMode = false;
      if (SCRIPTS_WITH_RUN_LOG.includes(newVal)) {
        this.loadRunLog();
      }
    },
  },
  methods: {
    ...mapActions('importStatus', ['additionalScript', 'loadAdditionalScriptRunLog']),
    formatDate(iso) {
      if (!iso) {
        return '';
      }
      return new Date(iso).toLocaleString();
    },
    async loadRunLog() {
      this.isLoadingLog = true;
      try {
        const entries = await this.loadAdditionalScriptRunLog({ script: this.selectedScript });
        this.runLog = (entries || []).map((entry, index) => ({ ...entry, _rowKey: index }));
      } finally {
        this.isLoadingLog = false;
      }
    },
    async runScript() {
      if (!this.selectedScript) {
        this.$store.dispatch('flashWarn', 'Please select a script to run.', { root: true });
        return;
      }

      const county = this.countyName || 'unknown';
      const collectionName = (this.showFixModeToggle && this.fixMode)
        ? SCRIPT_FIX_MODE_MAP[this.selectedScript]
        : this.selectedScript;
      if (!this.effectiveIsReadOnly) {
        const confirmed = window.confirm(`Are you sure you want to run "${collectionName}" against county "${county}"? This writes to the database and cannot be undone from this screen.`);
        if (!confirmed) {
          return;
        }
      }

      this.scriptError = '';
      this.resultRows = [];
      this.isRunning = true;

      const criteria = { collectionName, county };
      if (this.showDateFilters) {
        if (this.fromDate) {
          criteria.fromDate = this.fromDate;
        }
        if (this.toDate) {
          criteria.toDate = this.toDate;
        }
      }
      if (this.showYearRangeFilter && this.selectedYearRange && this.selectedYearRange.fromYear) {
        criteria.fromYear = this.selectedYearRange.fromYear;
        criteria.toYear = this.selectedYearRange.toYear;
      }

      try {
        const result = await this.additionalScript({ criteria });
        if (result && !result.status) {
          this.scriptError = result.error || result.message || 'An error occurred while running the script.';
        }
        this.resultRows = ((result && result.rows) || []).map((row, index) => ({ ...row, _rowKey: index }));
      } catch (error) {
        this.scriptError = error.message || 'An error occurred while running the script.';
      } finally {
        this.isRunning = false;
        if (this.showRunLog) {
          await this.loadRunLog();
        }
      }
    },
  },
};
</script>
